/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * SkillForge Review — admin reviewer for teacher-answer traces.
 *
 * Backend proxy: /api/admin/skillforge/review/* → V2 skillforge-service.
 * Approving a trace triggers a Wiki publish on the V2 side (button copy
 * says so explicitly).
 *
 * adapter_attempt is always NULL today — the observe pipeline isn't wired
 * yet.  The panel renders a labelled "Not captured" state so it doesn't
 * look broken.
 *
 * V2 is on Render free tier — first call can take ~30s.  Timeout is 60s
 * (30s adminApi default doubled here) and the initial fetch shows a
 * cold-start notice.
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import './SkillForgeReview.css';

const VERTICALS = [
  { value: '',        label: 'All verticals' },
  { value: 'iseries', label: 'iSeries' },
  { value: 'wms',     label: 'WMS' },
  { value: 'civil',   label: 'Civil' },
  { value: 'apparel', label: 'Apparel' },
  { value: 'erpnext', label: 'ERPNext' },
];

// Source values match the V2 skillforge_trace_source enum.
// Filter is include-only — to exclude live_fallback, pick one of the
// other three (or filter by 'All' and inspect the badges).
const SOURCES = [
  { value: '',                  label: 'All sources' },
  { value: 'live_fallback',     label: 'Live fallback' },
  { value: 'authored_scenario', label: 'Authored' },
  { value: 'user_correction',   label: 'Correction' },
  { value: 'seal_candidate',    label: 'SEAL candidate' },
];

const PAGE_LIMIT = 25;

// 60s override — adminApi default is 30s.  V2 cold start on Render free
// tier can take ~30s; give one call enough headroom to wake it.
const COLD_START_TIMEOUT = 60000;

// ─── formatting helpers ──────────────────────────────────────────────

function formatScore(n) {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return v.toFixed(2);
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function verticalLabel(v) {
  const hit = VERTICALS.find((x) => x.value === v);
  return hit ? hit.label : (v || '—');
}

function sourceLabel(s) {
  const hit = SOURCES.find((x) => x.value === s);
  return hit && hit.value ? hit.label : (s || '—');
}

function extractError(err) {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    'Unknown error'
  );
}

// ─── main component ──────────────────────────────────────────────────

const SkillForgeReview = () => {
  // Queue state
  const [traces, setTraces] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [vertical, setVertical] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState(null);
  const [firstLoadDone, setFirstLoadDone] = useState(false);

  // Detail state
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Decision state
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);
  const [decisionError, setDecisionError] = useState(null);
  const [conflictBanner, setConflictBanner] = useState(false);
  const [notes, setNotes] = useState('');
  const [editing, setEditing] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState('');

  // ── data fetching ──

  const fetchQueue = useCallback(async (newOffset = 0) => {
    setQueueLoading(true);
    setQueueError(null);
    try {
      // status=pending is explicit — the queue is the pending-review list;
      // don't rely on the V2 default matching that expectation.
      const params = { status: 'pending', limit: PAGE_LIMIT, offset: newOffset };
      if (vertical) params.vertical = vertical;
      if (sourceFilter) params.source = sourceFilter;
      const res = await adminApi.get('/api/admin/skillforge/review/pending', {
        params,
        timeout: COLD_START_TIMEOUT,
      });
      // V2 response is FLAT: { success, total, limit, offset, items }.
      // Items array is named `items`, not `traces`.
      const data = res?.data || {};
      setTraces(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
      setOffset(Number(data.offset ?? newOffset));
    } catch (err) {
      setTraces([]);
      setTotal(0);
      setQueueError(extractError(err));
    } finally {
      setQueueLoading(false);
      setFirstLoadDone(true);
    }
  }, [vertical, sourceFilter]);

  const fetchDetail = useCallback(async (traceId) => {
    setDetailLoading(true);
    setDetailError(null);
    setConflictBanner(false);
    try {
      const res = await adminApi.get(
        `/api/admin/skillforge/review/${encodeURIComponent(traceId)}`,
        { timeout: COLD_START_TIMEOUT }
      );
      setDetail(res?.data || null);
    } catch (err) {
      setDetail(null);
      setDetailError(extractError(err));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Initial + vertical-change reload
  useEffect(() => {
    fetchQueue(0);
  }, [fetchQueue]);

  // Reset per-decision UI when selection changes
  useEffect(() => {
    setNotes('');
    setEditing(false);
    setEditedAnswer('');
    setDecisionError(null);
    setConflictBanner(false);
  }, [selectedId]);

  // ── actions ──

  const handleSelect = (traceId) => {
    setSelectedId(traceId);
    fetchDetail(traceId);
  };

  const handleVerticalChange = (v) => {
    setVertical(v);
    setOffset(0);
    setSelectedId(null);
    setDetail(null);
  };

  const handleSourceChange = (s) => {
    setSourceFilter(s);
    setOffset(0);
    setSelectedId(null);
    setDetail(null);
  };

  const submitDecision = async (decision, extra = {}) => {
    if (!selectedId) return;
    setDecisionSubmitting(true);
    setDecisionError(null);
    setConflictBanner(false);
    try {
      const body = { decision };
      if (notes.trim()) body.notes = notes.trim();
      if (decision === 'edit') body.edited_answer = extra.edited_answer;

      const res = await adminApi.post(
        `/api/admin/skillforge/review/${encodeURIComponent(selectedId)}`,
        body,
        { timeout: COLD_START_TIMEOUT, validateStatus: (s) => s < 500 }
      );

      if (res.status === 409) {
        setConflictBanner(true);
        // Refetch queue + detail so the operator sees current state.
        await Promise.all([fetchQueue(offset), fetchDetail(selectedId)]);
        return;
      }
      if (res.status >= 400) {
        setDecisionError(extractError({ response: res }));
        return;
      }

      // Success — clear selection and reload queue.  The V2 side has now
      // started a Wiki publish for approve/edit; nothing to poll here.
      setSelectedId(null);
      setDetail(null);
      await fetchQueue(offset);
    } catch (err) {
      setDecisionError(extractError(err));
    } finally {
      setDecisionSubmitting(false);
    }
  };

  const handleApprove   = () => submitDecision('accept');
  const handleReject    = () => submitDecision('reject');
  const handleStartEdit = () => {
    setEditing(true);
    setEditedAnswer(detail?.teacher_answer || '');
  };
  const handleSubmitEdit = () => {
    if (!editedAnswer.trim()) {
      setDecisionError('Edited answer cannot be empty');
      return;
    }
    submitDecision('edit', { edited_answer: editedAnswer.trim() });
  };
  const handleCancelEdit = () => {
    setEditing(false);
    setEditedAnswer('');
  };

  // ── pagination ──

  const canPrev = offset > 0;
  const canNext = offset + traces.length < total;
  const goPrev = () => canPrev && fetchQueue(Math.max(0, offset - PAGE_LIMIT));
  const goNext = () => canNext && fetchQueue(offset + PAGE_LIMIT);

  // ── render ──

  return (
    <AdminLayout>
      <div className="skillforge-review">
        <header className="sfr-header">
          <div>
            <h1>SkillForge Review</h1>
            <p className="sfr-subtitle">
              Approve teacher answers before they become training data.
              Approving also publishes to Wiki on the V2 side.
            </p>
          </div>
          <div className="sfr-filters">
            <label htmlFor="sfr-vertical">Vertical</label>
            <select
              id="sfr-vertical"
              value={vertical}
              onChange={(e) => handleVerticalChange(e.target.value)}
              disabled={queueLoading}
            >
              {VERTICALS.map((v) => (
                <option key={v.value || 'all'} value={v.value}>{v.label}</option>
              ))}
            </select>
            <label htmlFor="sfr-source">Source</label>
            <select
              id="sfr-source"
              value={sourceFilter}
              onChange={(e) => handleSourceChange(e.target.value)}
              disabled={queueLoading}
              title="Include-only filter — pick a value to see just that source"
            >
              {SOURCES.map((s) => (
                <option key={s.value || 'all'} value={s.value}>{s.label}</option>
              ))}
            </select>
            <button
              type="button"
              className="sfr-btn sfr-btn-secondary"
              onClick={() => fetchQueue(offset)}
              disabled={queueLoading}
            >
              Refresh
            </button>
          </div>
        </header>

        {!firstLoadDone && queueLoading && (
          <div className="sfr-banner sfr-banner-info">
            Waking skillforge-service — first call after idle can take up to 45s.
          </div>
        )}

        <div className="sfr-layout">
          {/* ── LEFT: queue ── */}
          <aside className="sfr-queue">
            <div className="sfr-queue-header">
              <span className="sfr-queue-count">
                {queueLoading ? 'Loading…' : `${total} pending`}
              </span>
              <div className="sfr-pager">
                <button
                  type="button"
                  className="sfr-btn sfr-btn-ghost"
                  onClick={goPrev}
                  disabled={!canPrev || queueLoading}
                >‹ Prev</button>
                <button
                  type="button"
                  className="sfr-btn sfr-btn-ghost"
                  onClick={goNext}
                  disabled={!canNext || queueLoading}
                >Next ›</button>
              </div>
            </div>

            {queueError && (
              <div className="sfr-banner sfr-banner-error">
                Queue error: {queueError}
              </div>
            )}

            {!queueLoading && traces.length === 0 && !queueError && (
              <div className="sfr-empty">
                No pending traces
                {vertical ? ` for ${verticalLabel(vertical)}` : ''}
                {sourceFilter ? ` from ${sourceLabel(sourceFilter)}` : ''}.
              </div>
            )}

            <ul className="sfr-queue-list">
              {traces.map((t) => {
                const id = t.id;
                const active = id === selectedId;
                const flags = Array.isArray(t.quality_flags) ? t.quality_flags : [];
                return (
                  <li
                    key={id}
                    className={`sfr-queue-item${active ? ' sfr-queue-item-active' : ''}`}
                    onClick={() => handleSelect(id)}
                  >
                    <div className="sfr-queue-item-top">
                      <span className={`sfr-tag sfr-tag-${t.vertical || 'unknown'}`}>
                        {verticalLabel(t.vertical)}
                      </span>
                      {t.source && (
                        <span
                          className={`sfr-source sfr-source-${t.source}`}
                          title="Origin of this trace"
                        >
                          {sourceLabel(t.source)}
                        </span>
                      )}
                      <span className="sfr-queue-item-date">
                        {formatTimestamp(t.created_at)}
                      </span>
                    </div>
                    <div className="sfr-queue-item-question">
                      {t.question || '(no question)'}
                    </div>
                    <div className="sfr-queue-item-scores">
                      <span title="SEAL quality score">
                        SEAL {formatScore(t.seal_quality_score)}
                      </span>
                      <span title="Teacher consensus score">
                        · Consensus {formatScore(t.consensus_score)}
                      </span>
                      {t.teacher_id && (
                        <span
                          className="sfr-queue-item-teacher"
                          title="Teacher / model that produced the answer"
                        >
                          · {t.teacher_id}
                        </span>
                      )}
                    </div>
                    {flags.length > 0 && (
                      <div className="sfr-queue-item-flags">
                        {flags.map((f) => (
                          <span key={f} className="sfr-flag" title="Quality flag">
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* ── RIGHT: detail ── */}
          <section className="sfr-detail">
            {!selectedId && (
              <div className="sfr-detail-empty">
                Select a trace from the queue to review it.
              </div>
            )}

            {selectedId && detailLoading && (
              <div className="sfr-detail-loading">Loading trace…</div>
            )}

            {selectedId && detailError && (
              <div className="sfr-banner sfr-banner-error">
                Failed to load trace: {detailError}
              </div>
            )}

            {selectedId && detail && !detailLoading && (
              <>
                {conflictBanner && (
                  <div className="sfr-banner sfr-banner-warn">
                    This trace is no longer pending — someone else already
                    reviewed it.  Queue refreshed.
                  </div>
                )}

                <div className="sfr-detail-header">
                  <div>
                    <span className={`sfr-tag sfr-tag-${detail.vertical || 'unknown'}`}>
                      {verticalLabel(detail.vertical)}
                    </span>
                    {detail.source && (
                      <span className={`sfr-source sfr-source-${detail.source}`}>
                        {sourceLabel(detail.source)}
                      </span>
                    )}
                    <span className="sfr-detail-id">
                      trace {detail.id || selectedId}
                      {detail.teacher_id ? ` · teacher ${detail.teacher_id}` : ''}
                    </span>
                  </div>
                  <div className="sfr-detail-scores">
                    <span>SEAL <b>{formatScore(detail.seal_quality_score)}</b></span>
                    <span>Consensus <b>{formatScore(detail.consensus_score)}</b></span>
                  </div>
                </div>

                <section className="sfr-panel">
                  <h3>Question</h3>
                  <div className="sfr-panel-body sfr-panel-question">
                    {detail.question || '—'}
                  </div>
                </section>

                <section className="sfr-panel">
                  <h3>Grounding / context</h3>
                  <div className="sfr-panel-body sfr-panel-context">
                    {renderContext(detail.context)}
                  </div>
                </section>

                {/* Reasoning trace — the main thing being judged.
                    Given the most space intentionally. Column name in the
                    V2 skillforge_traces table is `teacher_reasoning_trace`. */}
                <section className="sfr-panel sfr-panel-reasoning">
                  <h3>Reasoning trace</h3>
                  <div className="sfr-panel-body sfr-reasoning-body">
                    {detail.teacher_reasoning_trace
                      ? <pre>{detail.teacher_reasoning_trace}</pre>
                      : <em className="sfr-muted">No reasoning trace on this row.</em>}
                  </div>
                </section>

                {Array.isArray(detail.quality_flags) && detail.quality_flags.length > 0 && (
                  <section className="sfr-panel">
                    <h3>Quality flags</h3>
                    <div className="sfr-panel-body sfr-flag-row">
                      {detail.quality_flags.map((f) => (
                        <span key={f} className="sfr-flag">{f}</span>
                      ))}
                    </div>
                  </section>
                )}

                <section className="sfr-panel">
                  <h3>Teacher answer</h3>
                  <div className="sfr-panel-body">
                    {detail.teacher_answer
                      ? <pre>{detail.teacher_answer}</pre>
                      : <em className="sfr-muted">No teacher answer.</em>}
                  </div>
                </section>

                <section className="sfr-panel sfr-panel-adapter">
                  <h3>Adapter attempt</h3>
                  <div className="sfr-panel-body">
                    {detail.adapter_attempt
                      ? <pre>{detail.adapter_attempt}</pre>
                      : (
                        <div className="sfr-not-captured">
                          <strong>Not captured.</strong> V1 doesn't call the
                          observe endpoint yet, so no adapter attempt was
                          recorded for this trace.  This column will populate
                          once observe is wired.
                        </div>
                      )}
                  </div>
                </section>

                {/* Decision area */}
                <section className="sfr-decision">
                  {decisionError && (
                    <div className="sfr-banner sfr-banner-error">
                      {decisionError}
                    </div>
                  )}

                  <label className="sfr-notes-label" htmlFor="sfr-notes">
                    Review notes (optional)
                  </label>
                  <textarea
                    id="sfr-notes"
                    className="sfr-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Why are you accepting / rejecting / editing this?"
                    rows={2}
                    disabled={decisionSubmitting || editing}
                  />

                  {!editing && (
                    <div className="sfr-actions">
                      <button
                        type="button"
                        className="sfr-btn sfr-btn-approve"
                        onClick={handleApprove}
                        disabled={decisionSubmitting}
                        title="Publishes to Wiki on the V2 side"
                      >
                        Approve &amp; publish to Wiki
                      </button>
                      <button
                        type="button"
                        className="sfr-btn sfr-btn-edit"
                        onClick={handleStartEdit}
                        disabled={decisionSubmitting}
                      >
                        Edit answer…
                      </button>
                      <button
                        type="button"
                        className="sfr-btn sfr-btn-reject"
                        onClick={handleReject}
                        disabled={decisionSubmitting}
                      >
                        Reject
                      </button>
                    </div>
                  )}

                  {editing && (
                    <div className="sfr-edit-area">
                      <label className="sfr-notes-label" htmlFor="sfr-edited">
                        Edited answer (submitted as decision <code>edit</code>)
                      </label>
                      <textarea
                        id="sfr-edited"
                        className="sfr-edited"
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        rows={12}
                        disabled={decisionSubmitting}
                      />
                      <div className="sfr-actions">
                        <button
                          type="button"
                          className="sfr-btn sfr-btn-approve"
                          onClick={handleSubmitEdit}
                          disabled={decisionSubmitting || !editedAnswer.trim()}
                          title="Submits your edited answer and publishes to Wiki"
                        >
                          Submit edit &amp; publish to Wiki
                        </button>
                        <button
                          type="button"
                          className="sfr-btn sfr-btn-secondary"
                          onClick={handleCancelEdit}
                          disabled={decisionSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {decisionSubmitting && (
                    <div className="sfr-decision-pending">
                      Submitting decision — this can take up to 45s if
                      skillforge-service is cold…
                    </div>
                  )}
                </section>
              </>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
};

// context can be either a plain string or a JSON blob.  Render both
// forms readably without assuming a shape.
function renderContext(ctx) {
  if (ctx === null || ctx === undefined || ctx === '') {
    return <em className="sfr-muted">No context recorded.</em>;
  }
  if (typeof ctx === 'string') {
    return <pre>{ctx}</pre>;
  }
  try {
    return <pre>{JSON.stringify(ctx, null, 2)}</pre>;
  } catch {
    return <pre>{String(ctx)}</pre>;
  }
}

export default SkillForgeReview;
